import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import './QuestionsTab.css';

const DROPDOWN_MAX = 100;

function filterQuestionsByText(list, search) {
  const s = (search || '').trim().toLowerCase();
  if (!s) return list.slice(0, DROPDOWN_MAX);
  return list.filter((q) => ((q.text || '') + ' ' + (q.id || '')).toLowerCase().includes(s)).slice(0, DROPDOWN_MAX);
}

function filterUsersById(list, search) {
  const s = (search || '').trim().toLowerCase();
  if (!s) return list.slice(0, DROPDOWN_MAX);
  return list.filter(
    (u) =>
      String(u.id || '').toLowerCase().includes(s) ||
      String(u.telegram_id || '').toLowerCase().includes(s)
  ).slice(0, DROPDOWN_MAX);
}

const API_URL = process.env.REACT_APP_API_URL || '';

function UserQuestionAnswersSubTab() {
  const [answers, setAnswers] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filterBy, setFilterBy] = useState(null); // 'question' | 'user' | null
  const [selectedQuestionId, setSelectedQuestionId] = useState('');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [questionSearchText, setQuestionSearchText] = useState('');
  const [userSearchText, setUserSearchText] = useState('');
  const [questionDropdownOpen, setQuestionDropdownOpen] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingAnswer, setEditingAnswer] = useState(null);
  const [formData, setFormData] = useState({
    question_id: '',
    user_id: '',
    answer_text: '',
    is_correct: 0,
    rating: '',
  });
  const [editModal, setEditModal] = useState(null); // { id, answer_text, is_correct }

  useEffect(() => {
    fetchQuestions();
    fetchUsers();
  }, []);

  const filteredQuestions = useMemo(
    () => filterQuestionsByText(questions, questionSearchText),
    [questions, questionSearchText]
  );
  const filteredUsers = useMemo(
    () => filterUsersById(users, userSearchText),
    [users, userSearchText]
  );

  const fetchQuestions = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/questions`, { params: { limit: 5000 } });
      setQuestions(res.data);
    } catch (err) {
      console.error('Ошибка загрузки вопросов:', err);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/users`, { params: { limit: 5000 } });
      setUsers(res.data);
    } catch (err) {
      console.error('Ошибка загрузки пользователей:', err);
    }
  };

  const fetchByQuestion = async () => {
    if (!selectedQuestionId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API_URL}/api/user-question-answers`, {
        params: { question_id: parseInt(selectedQuestionId, 10), limit: 500 },
      });
      setAnswers(res.data);
      setFilterBy('question');
    } catch (err) {
      setError('Ошибка загрузки: ' + (err.response?.data?.detail || err.message));
      setAnswers([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchByUser = async () => {
    if (!selectedUserId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API_URL}/api/user-question-answers`, {
        params: { user_id: parseInt(selectedUserId, 10), limit: 500 },
      });
      setAnswers(res.data);
      setFilterBy('user');
    } catch (err) {
      setError('Ошибка загрузки: ' + (err.response?.data?.detail || err.message));
      setAnswers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    try {
      const payload = {
        question_id: parseInt(formData.question_id, 10),
        user_id: parseInt(formData.user_id, 10),
        answer_text: formData.answer_text || null,
        is_correct: formData.is_correct === 1 || formData.is_correct === '1' ? 1 : 0,
      };
      if (formData.rating !== '' && formData.rating != null) {
        payload.rating = parseInt(formData.rating, 10);
      }
      await axios.post(`${API_URL}/api/user-question-answers`, payload);
      setShowForm(false);
      setFormData({ question_id: '', user_id: '', answer_text: '', is_correct: 0, rating: '' });
      if (filterBy === 'question' && selectedQuestionId) fetchByQuestion();
      else if (filterBy === 'user' && selectedUserId) fetchByUser();
    } catch (err) {
      setError('Ошибка сохранения: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editModal) return;
    setError(null);
    try {
      await axios.put(`${API_URL}/api/user-question-answers/${editModal.id}`, {
        answer_text: editModal.answer_text,
        is_correct: editModal.is_correct,
      });
      setEditModal(null);
      if (filterBy === 'question' && selectedQuestionId) fetchByQuestion();
      else if (filterBy === 'user' && selectedUserId) fetchByUser();
    } catch (err) {
      setError('Ошибка сохранения: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Удалить эту запись?')) return;
    setError(null);
    try {
      await axios.delete(`${API_URL}/api/user-question-answers/${id}`);
      if (filterBy === 'question' && selectedQuestionId) fetchByQuestion();
      else if (filterBy === 'user' && selectedUserId) fetchByUser();
    } catch (err) {
      setError('Ошибка удаления: ' + (err.response?.data?.detail || err.message));
    }
  };

  const openEdit = (a) => {
    setEditModal({ id: a.id, answer_text: a.answer_text || '', is_correct: a.is_correct });
  };

  return (
    <div className="questions-list-tab user-question-answers-sub-tab">
      <div className="tab-header">
        <h3>Ответы пользователей на вопросы</h3>
        <div className="tab-header-actions">
          <button
            className="btn btn-primary"
            onClick={() => {
              setShowForm(!showForm);
              if (showForm) setFormData({ question_id: '', user_id: '', answer_text: '', is_correct: 0, rating: '' });
            }}
          >
            {showForm ? '− Скрыть форму' : '+ Добавить запись'}
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="filters-container" style={{ marginBottom: '1rem' }}>
        <div className="filters-row">
          <div className="filter-group">
            <label>Ответы по вопросу</label>
            <div className="filter-combobox">
              <input
                type="text"
                className="filter-input"
                placeholder="Введите текст вопроса..."
                value={questionSearchText}
                onChange={(e) => {
                  setQuestionSearchText(e.target.value);
                  setSelectedQuestionId('');
                  setQuestionDropdownOpen(true);
                }}
                onFocus={() => setQuestionDropdownOpen(true)}
                onBlur={() => setTimeout(() => setQuestionDropdownOpen(false), 150)}
              />
              {questionDropdownOpen && (
                <div className="filter-combobox-dropdown" role="listbox">
                  {filteredQuestions.length === 0 ? (
                    <div className="filter-combobox-dropdown-empty">Нет совпадений</div>
                  ) : (
                    filteredQuestions.map((q) => (
                      <div
                        key={q.id}
                        role="option"
                        tabIndex={0}
                        className="filter-combobox-dropdown-item"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          setSelectedQuestionId(String(q.id));
                          setQuestionSearchText(q.text && q.text.length > 80 ? q.text.slice(0, 80) + '…' : (q.text || `#${q.id}`));
                          setQuestionDropdownOpen(false);
                        }}
                      >
                        {q.id}: {q.text && q.text.length > 60 ? q.text.slice(0, 60) + '…' : (q.text || '—')}
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
            <button
              type="button"
              className="btn btn-primary"
              onClick={fetchByQuestion}
              disabled={!selectedQuestionId || loading}
              style={{ marginTop: '0.5rem' }}
            >
              Показать
            </button>
          </div>
          <div className="filter-group">
            <label>Ответы по пользователю</label>
            <div className="filter-combobox">
              <input
                type="text"
                className="filter-input"
                placeholder="Введите ID или telegram_id..."
                value={userSearchText}
                onChange={(e) => {
                  setUserSearchText(e.target.value);
                  setSelectedUserId('');
                  setUserDropdownOpen(true);
                }}
                onFocus={() => setUserDropdownOpen(true)}
                onBlur={() => setTimeout(() => setUserDropdownOpen(false), 150)}
              />
              {userDropdownOpen && (
                <div className="filter-combobox-dropdown" role="listbox">
                  {filteredUsers.length === 0 ? (
                    <div className="filter-combobox-dropdown-empty">Нет совпадений</div>
                  ) : (
                    filteredUsers.map((u) => (
                      <div
                        key={u.id}
                        role="option"
                        tabIndex={0}
                        className="filter-combobox-dropdown-item"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          setSelectedUserId(String(u.id));
                          setUserSearchText(`ID ${u.id} (tg: ${u.telegram_id})`);
                          setUserDropdownOpen(false);
                        }}
                      >
                        ID {u.id} (tg: {u.telegram_id})
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
            <button
              type="button"
              className="btn btn-primary"
              onClick={fetchByUser}
              disabled={!selectedUserId || loading}
              style={{ marginTop: '0.5rem' }}
            >
              Показать
            </button>
          </div>
        </div>
      </div>

      {showForm && (
        <div className="form-container form-container-inline">
          <h4>Добавить запись</h4>
          <form onSubmit={handleAddSubmit}>
            <div className="form-group">
              <label>Вопрос *</label>
              <select
                value={formData.question_id}
                onChange={(e) => setFormData({ ...formData, question_id: e.target.value })}
                required
              >
                <option value="">Выберите вопрос</option>
                {questions.map((q) => (
                  <option key={q.id} value={q.id}>
                    {q.id}: {(q.text || '').slice(0, 60)}{(q.text && q.text.length > 60) ? '…' : ''}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Пользователь *</label>
              <select
                value={formData.user_id}
                onChange={(e) => setFormData({ ...formData, user_id: e.target.value })}
                required
              >
                <option value="">Выберите пользователя</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    ID {u.id} (telegram: {u.telegram_id})
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Текст ответа</label>
              <textarea
                value={formData.answer_text}
                onChange={(e) => setFormData({ ...formData, answer_text: e.target.value })}
                rows={3}
                placeholder="Текст ответа"
              />
            </div>
            <div className="form-group">
              <label>Правильный ответ</label>
              <div>
                <label style={{ marginRight: '1rem' }}>
                  <input
                    type="radio"
                    name="is_correct_add"
                    checked={formData.is_correct === 0}
                    onChange={() => setFormData({ ...formData, is_correct: 0 })}
                  />
                  {' '}Нет
                </label>
                <label>
                  <input
                    type="radio"
                    name="is_correct_add"
                    checked={formData.is_correct === 1}
                    onChange={() => setFormData({ ...formData, is_correct: 1 })}
                  />
                  {' '}Да
                </label>
              </div>
            </div>
            <div className="form-group">
              <label>Оценка (число)</label>
              <input
                type="number"
                value={formData.rating}
                onChange={(e) => setFormData({ ...formData, rating: e.target.value })}
                placeholder="Необязательно"
              />
            </div>
            <div className="form-actions">
              <button type="submit" className="btn btn-primary">Создать</button>
              <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>Отмена</button>
            </div>
          </form>
        </div>
      )}

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Вопрос (id)</th>
              <th>Пользователь (tg)</th>
              <th>Текст ответа</th>
              <th>Оценка</th>
              <th>Правильный</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="7" className="empty-state">Загрузка...</td></tr>
            ) : answers.length === 0 ? (
              <tr>
                <td colSpan="7" className="empty-state">
                  {filterBy ? 'Нет записей по выбранному фильтру' : 'Выберите «Ответы по вопросу» или «Ответы по пользователю» и нажмите Показать'}
                </td>
              </tr>
            ) : (
              answers.map((a) => (
                <tr key={a.id}>
                  <td>{a.id}</td>
                  <td title={a.question_text || ''}>
                    {a.question_id}
                    {a.question_text && (
                      <span className="cell-short" title={a.question_text}>
                        {' '}({a.question_text.length > 30 ? a.question_text.slice(0, 30) + '…' : a.question_text})
                      </span>
                    )}
                  </td>
                  <td>{a.user_telegram_id != null ? a.user_telegram_id : a.user_id}</td>
                  <td className="question-text">{(a.answer_text || '—').slice(0, 200)}{(a.answer_text && a.answer_text.length > 200) ? '…' : ''}</td>
                  <td>{a.rating != null ? a.rating : '—'}</td>
                  <td>{a.is_correct ? 'Да' : 'Нет'}</td>
                  <td>
                    <button type="button" className="btn-icon btn-edit" onClick={() => openEdit(a)} title="Редактировать">✏️</button>
                    <button type="button" className="btn-icon btn-delete" onClick={() => handleDelete(a.id)} title="Удалить">✕</button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {editModal && (
        <div className="reorder-modal-overlay" onClick={() => setEditModal(null)}>
          <div className="reorder-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Редактировать ответ</h3>
            <form onSubmit={handleEditSubmit}>
              <div className="form-group">
                <label>Текст ответа</label>
                <textarea
                  value={editModal.answer_text}
                  onChange={(e) => setEditModal({ ...editModal, answer_text: e.target.value })}
                  rows={4}
                  style={{ width: '100%' }}
                />
              </div>
              <div className="form-group">
                <label>Правильный</label>
                <div>
                  <label style={{ marginRight: '1rem' }}>
                    <input
                      type="radio"
                      name="is_correct_edit"
                      checked={editModal.is_correct === 0}
                      onChange={() => setEditModal({ ...editModal, is_correct: 0 })}
                    />
                    {' '}Нет
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="is_correct_edit"
                      checked={editModal.is_correct === 1}
                      onChange={() => setEditModal({ ...editModal, is_correct: 1 })}
                    />
                    {' '}Да
                  </label>
                </div>
              </div>
              <div className="reorder-modal-actions" style={{ marginTop: '1rem' }}>
                <button type="submit" className="btn btn-primary">Сохранить</button>
                <button type="button" className="btn btn-secondary" onClick={() => setEditModal(null)}>Отмена</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserQuestionAnswersSubTab;
